<template>
  <Multiselect
    @input="$emit('input', $event)"
    @select="handleSelect"
    @keyup.enter="handleOpen"
    @keyup.esc="handleClose"
    ref="combobox"
    :id="id"
    :value="value"
    :options="options"
    :label="labelProp"
    :valueProp="valueProp"
    :placeholder="placeholder"
    :noOptionsText="noOptions"
    :noResultsText="noResults"
    :searchable="true"
    :canClear="canClear"
    :caret="showCaret"
    :canDeselect="false"
    :classes="{
      container: `l-Combobox combobox relative w-full outline-none font-normal text-black
        flex items-center justify-end`,
      wrapper: `combobox-wrapper relative h-ml w-full px-md
        flex items-center justify-between
        font-normal bg-whiteOverride rounded border border-solid border-border`,
      singleLabel: `combobox-single-label absolute w-full pr-ml ${caretSpace} ${clearSpace} ${caretClearSpace}
        truncate overflow-ellipsis pointer-events-none`,
      search: `combobox-search flex-grow h-ml p-0 m-0
        bg-transparent border-none outline-none`,
      assist: 'combobox-assistive-text hidden',
      placeholder: `combobox-placeholder absolute pointer-events-none
        text-lightGray`,
      dropdown: 'combobox-dropdown relative z-100',
      options: `combobox-options absolute w-full max-h-comboBox overflow-auto p-0 py-xs m-0
        bg-whiteOverride rounded !rounded-t-none
        border border-t-0 border-solid border-primaryGradient empty:hidden`,
      option: `combobox-option list-none cursor-pointer font-normal px-md`,
      optionPointed: 'is-pointed !bg-gray-50',
      optionSelectedPointed: 'is-selected is-pointed !bg-gray-100',
      noOptions: `combobox-no-options absolute w-full max-h-comboBox p-base px-md m-0
        overflow-scroll bg-whiteOverride rounded !rounded-t-none
        border border-t-0 border-solid border-primaryGradient`,
      noResults: `combobox-no-results absolute w-full max-h-comboBox p-base px-md m-0
        overflow-scroll bg-whiteOverride rounded !rounded-t-none
        border border-t-0 border-solid border-primaryGradient`,
    }"
    :data-ec="dataEc"
    :data-et="dataEt"
  >
    <template v-slot:placeholder>
      <div
        :class="`combobox-placeholder absolute w-full h-full flex items-center pr-ml ${caretSpace} text-primaryGrey`"
        aria-hidden="true"
        @mousedown="handleOpen"
      >
        <span class="min-w-0 truncate overflow-ellipsis">
          {{ placeholder }}
        </span>
      </div>
    </template>

    <template v-slot:singlelabel="{ value }">
      <div
        :class="`combobox-single-label absolute w-full h-full flex items-center align-middle pr-ml ${caretSpace} ${clearSpace} ${caretClearSpace}`"
        @mousedown="handleOpen"
      >
        <span class="min-w-0 truncate overflow-ellipsis">
          {{ value[labelProp] }}
        </span>
      </div>
    </template>

    <template v-slot:clear>
      <IconButton
        icon="close"
        variant="invisible"
        class="w-lg h-lg !p-0 -mr-sm peer"
        @mousedown.native="handleClear"
        :data-ec="dataEc"
        :data-et="`${dataEt}_clear`"
        data-test="clear"
      />
    </template>

    <template v-slot:caret>
      <IconButton
        icon="caret-down"
        variant="invisible"
        class="w-ml h-ml !p-0 -mr-base peer-read-only:ml-sm"
        @click="handleOpen"
        data-test="caret"
      />
    </template>

    <template v-slot:option="{ option }">
      <div
        class="flex items-center justify-between"
        @click="handleClose"
        :data-ec="dataEc"
        :data-et="`${dataEt}_option`"
      >
        <div class="my-sm truncate overflow-ellipsis">
          {{ option[labelProp] }}
        </div>
        <slot
          v-if="combobox && combobox.isPointed(option)"
          name="action"
          v-bind:option="option"
        />
      </div>
    </template>
  </Multiselect>
</template>

<script>
// @ts-check
import { computed, defineComponent, ref, toRefs, watch } from 'vue';
import Multiselect from '@vueform/multiselect/dist/multiselect.vue2';

import useIncrementingId from '@/composables/incrementingId';
import i18n from '@/locales';

import IconButton from '@/components/library/buttons/IconButton.vue';

export default defineComponent({
  components: { Multiselect, IconButton },
  props: {
    value: {},
    // array of objects
    options: {
      type: Array,
      required: true,
    },
    // the property on the option that contains the label
    labelProp: {
      type: String,
      default: 'label',
    },
    // the property on the option that contains the value
    valueProp: {
      type: String,
      default: 'value',
    },
    placeholder: {
      type: String,
      default() {
        return i18n.t('ui.combobox.placeholder');
      },
    },
    noOptions: {
      type: String,
      default() {
        return i18n.t('ui.combobox.noOptions');
      },
    },
    noResults: {
      type: String,
      default() {
        return i18n.t('ui.combobox.noResults');
      },
    },
    showCaret: {
      type: Boolean,
      default: true,
    },
    canClear: {
      type: Boolean,
      default: true,
    },
    dataEc: {
      type: String,
      required: true,
    },
    dataEt: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const {
 options, valueProp, showCaret, canClear,
} = toRefs(props);

    const combobox = ref();

    const optionValues = computed(() =>
      options.value.map(option => option[valueProp.value]));
    watch(optionValues, (newOptions) => {
      if (
        combobox.value?.internalValue?.[valueProp.value] &&
        !newOptions.includes(combobox.value?.internalValue?.[valueProp.value])
      ) {
        combobox.value.clear();
      }
    });

    function handleOpen(event) {
      event.preventDefault();
      event.stopPropagation();
      combobox.value.open();
      combobox.value.focus();
    }
    function handleClose() {
      combobox.value.close();
      combobox.value.clearPointer();
    }
    function handleClear(event) {
      event.preventDefault();
      event.stopPropagation();

      combobox.value.clear();
    }

    function handleSelect(event) {
      emit('select', event);

      // hacky fix for input being focused after selecting
      combobox.value.$refs.input.blur();
    }

    // ensure unique ids if multiple instances on page
    const getId = useIncrementingId();
    const id = `combo-${getId()}`;

    // padding to create space for caret/clear buttons
    const caretSpace = computed(() => (showCaret.value ? ' !pr-16' : ''));
    const clearSpace = computed(() => (canClear.value ? ' !pr-16' : ''));
    const caretClearSpace = computed(() =>
      (showCaret.value && canClear.value ? ' !pr-24' : ''));

    return {
      combobox,
      id,
      handleOpen,
      handleClose,
      handleClear,
      handleSelect,
      caretSpace,
      clearSpace,
      caretClearSpace,
    };
  },
});
</script>

<style scoped>
.combobox.is-open :deep(.combobox-wrapper) {
  @apply !rounded-b-none border-primaryGradient;
}

/* force show dropdown, used in /designs/combo */
.combobox.force-open :deep(.combobox-dropdown) {
  display: block !important;
}
</style>
